import { component, OnInit, prop } from '@gbds/core/src/ts/core'

/**
 * @noInheritDoc
 */
@component('gb-button')
export class Button extends HTMLElement implements OnInit {
	anchorEl: HTMLAnchorElement
	buttonEl: HTMLButtonElement
	buttons: NodeListOf<HTMLButtonElement> | null = null
	bacAnchorEl: HTMLAnchorElement | null

	private _onDealerCookieUpdated = this.appendCookieToUrl.bind(this)

	/**
	 * Button link type.
	 */
	@prop()
	public dataLinkType: string

	/**
	 * Target Zone ID
	 */
	@prop()
	public dataTargetZoneId: string

	/**
	 * Target Zone Section ID
	 */
	@prop()
	public dataTargetZoneSectionId: string

	/**
	 * Target Zone ID for authenticated users
	 */
	@prop()
	public dataAuthTargetZoneId: string

	/**
	 * Target Zone Section ID for authenticated users
	 */
	@prop()
	public dataAuthTargetZoneSectionId: string

	/**
	 * Returns true if attribute 'data-append-current-query-params' exists on element
	 */
	get isAppendCurrentQueryParams(): boolean {
		return this.hasAttribute('data-append-current-query-params')
	}

	/**
	 * Returns true if attribute 'data-append-current-hash' exists on element
	 */
	get isAppendCurrentHash(): boolean {
		return this.hasAttribute('data-append-current-hash')
	}

	constructor() {
		super()
	}

	onInit(): void {
		// Store the buttons for reuse in isAuthenticated
		this.buttons = this.querySelectorAll('button')

		// Set up the button or anchor based on the link type
		if (this.dataLinkType === 'targetZone' && this.buttons) {
			this.buttons.forEach(button => {
				button.onclick = e => this.changeTargetZone(e)
			})
		} else {
			this.anchorEl = this.querySelector('a')

			if (this.isAppendCurrentQueryParams || this.isAppendCurrentHash) {
				this.appendToHref()
			}
		}

		this.bacAnchorEl = this.querySelector('a')
		if (this.bacAnchorEl?.hasAttribute('data-append-bac-cookie')) {
			this.appendCookieToUrl()
			window.addEventListener('dealerCookieUpdated', this._onDealerCookieUpdated)
		}
	}

	disconnectedCallback(): void {
		window.removeEventListener('dealerCookieUpdated', this._onDealerCookieUpdated)
	}

	appendCookieToUrl(): void {
		const selectedDealerCookie = document.cookie.split('; ').find(row => row.startsWith('gmwp.selected-dealer='))

		if (!selectedDealerCookie || !this.bacAnchorEl) {
			return
		}

		const bac = selectedDealerCookie.split('=')[1]
		// Already has ?bac= or &bac=
		if (/([?&])bac=/.test(this.bacAnchorEl.href)) {
			// Replace existing bac value
			this.bacAnchorEl.href = this.bacAnchorEl.href.replace(/([?&])bac=[^&#]*/, `$1bac=${bac}`)
		} else {
			// Append new bac param
			const joiner = this.bacAnchorEl.href.includes('?') ? '&' : '?'
			this.bacAnchorEl.href = `${this.bacAnchorEl.href}${joiner}bac=${bac}`
		}
	}

	/**
	 * Determines if the user is authenticated.
	 */
	isAuthenticated(button: HTMLButtonElement | null): boolean {
		if (!button) {
			return false
		}

		const hasAuthTargetZoneId = button?.hasAttribute('data-auth-target-zone-id')
		const hasAuthTargetZoneSectionId = button?.hasAttribute('data-auth-target-zone-section-id')

		if (!hasAuthTargetZoneId || !hasAuthTargetZoneSectionId) {
			return false
		}
		const authState = window.gbpe?.auth?.currentAuthState

		if (authState === undefined || authState === null || authState === -1) {
			return false
		}
		return authState === 1
	}

	/**
	 * Gets the target zone ID based on authentication status.
	 */
	getTargetZoneId(button: HTMLButtonElement): string {
		if (this.isAuthenticated(button)) {
			return this.dataAuthTargetZoneId
		} else {
			return this.dataTargetZoneId
		}
	}

	/**
	 * Gets the target zone section ID based on authentication status.
	 */
	getTargetZoneSectionId(button: HTMLButtonElement): string {
		if (this.isAuthenticated(button)) {
			return this.dataAuthTargetZoneSectionId
		} else {
			return this.dataTargetZoneSectionId
		}
	}

	/**
	 * Creates an event to change the target zone.
	 */
	changeTargetZone(e: Event): void {
		e.preventDefault()
		e.stopPropagation()

		const targetButton = e.currentTarget as HTMLButtonElement
		const eventOpts = {
			bubbles: true,
			cancelable: false,
			detail: {
				zoneID: this.getTargetZoneId(targetButton),
				targetZoneID: this.getTargetZoneSectionId(targetButton),
				sourceRoot: this.getRootNode(),
			},
		}
		try {
			const evt = new CustomEvent('targetZoneChange', eventOpts)
			window.dispatchEvent(evt)
		} catch (error) {
			console.error('Error creating targetZoneChange event:', error)
		}
	}

	/**
	 * Updates the href to add query params and/or hash values from the page url.
	 */
	appendToHref(): void {
		const currentPageQueryParams = window.location.search

		if (currentPageQueryParams && this.anchorEl) {
			let newHref = this.anchorEl.getAttribute('href')
			let hash = '#'
			const indexOfHash = newHref.indexOf('#')

			if (this.isAppendCurrentHash) {
				hash += window.location.hash.substr(1)
			}

			if (indexOfHash !== -1) {
				hash += newHref.substr(indexOfHash + 1)
				newHref = newHref.slice(0, indexOfHash)
			}

			if (this.isAppendCurrentQueryParams) {
				newHref += newHref.indexOf('?') === -1 ? '?' : '&'
				newHref += window.location.search.replace(/^\?/, '')
			}

			if (hash !== '#') {
				newHref += hash
			}

			this.anchorEl.href = newHref
		}
	}
}
